import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, TemplateRef, ViewChild, inject, input, output, signal } from '@angular/core';

import { Observable, map } from 'rxjs';

import { FontIconComponent } from '@camelot/icons';
import { Menu, MenuComponent, MenuIcon, MenuPanel } from '@camelot/menu';
import { CamSizes } from '@camelot/styles';
import { TranslatePipe } from '@camelot/translation';
import {
  ButtonComponent,
  EmptyComponent,
  ErrorComponent,
  InlineProfileDataComponent,
  LoaderComponent,
  UserLogoData,
} from '@camelot/ui';
import { CamBaseComponent, StopPropagationDirective } from '@camelot/utils';

import { CAM_AUTH_TOKEN } from '../../services/auth.service';
import { CamUsersService } from '../../services/users.service';
import { CamTranslationUser } from '../../translation.service';
import { SwitchLanguageComponent } from '../switch-language/switch-language.component';

@Component({
  selector: 'cam-my-account',
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss'],
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonComponent,
    InlineProfileDataComponent,
    SwitchLanguageComponent,
    StopPropagationDirective,
    FontIconComponent,
    LoaderComponent,
    ErrorComponent,
    EmptyComponent,
    MenuComponent,
  ],
})
export class MyAccountComponent extends CamBaseComponent implements AfterViewInit {
  infosMenu = input<Menu | null>(null);

  appVersion = input<string | null>(null);

  isEditable = input(false);

  navigateEvent = output();

  navigateEditEvent = output();

  @ViewChild('languageTemplate', { static: true })
  languageTemplate!: TemplateRef<any>;

  @ViewChild('infosTemplate', { static: true })
  infosTemplate!: TemplateRef<any>;

  private _authService = inject(CAM_AUTH_TOKEN);
  private _usersService = inject(CamUsersService);

  public profileMenu = signal<Menu | null>(null);
  public disconnectionMenu = signal<Menu | null>(null);

  public currentUser$ = this._usersService.currentUser.get$();

  public profile$ = this.currentUser$.pipe(
    map(data => ({
      email: data?.userName,
      title: {
        second: data?.firstName || data?.lastName,
      },
    }))
  );

  public userLogo$: Observable<{ user: UserLogoData; size?: CamSizes } | null> = this.currentUser$.pipe(
    map(x => {
      if (!x) {
        return null;
      }

      return {
        size: 'lg',
        user: {
          firstName: x.firstName || '',
          lastName: x.lastName || '',
          picture: x.picture,
          pictureDocumentId: x.pictureDocumentId,
          trigram: x.trigram || '',
        },
      };
    })
  );

  constructor() {
    super();
    CamTranslationUser.getInstance();
    this._fetch();
  }

  ngAfterViewInit() {
    this.profileMenu.set(this.getProfileMenu(this.languageTemplate, this.infosTemplate));
    this.disconnectionMenu.set(this.getDisconnectionMenu());
  }

  public navigateToProfile() {
    this.navigateEvent.emit();
  }

  public disconnect() {
    this._authService.logout().then(() => location.reload());
  }

  public getProfileMenu(languageTemplate: TemplateRef<any>, infosTemplate: TemplateRef<any>) {
    const menu = [
      new MenuPanel({
        key: 'language',
        label: 'user.language',
        order: 1,
        template: languageTemplate,
        style: 'dark',
        icon: 'language',
        endingIcon: 'arrow-big-right',
      }),
      new MenuPanel({
        key: 'infos',
        label: 'user.infos',
        order: 2,
        template: infosTemplate,
        style: 'dark',
        icon: 'infos',
        endingIcon: 'arrow-big-right',
      }),
      new MenuIcon({
        key: 'params',
        label: 'user.params',
        order: 3,
        disabled: true,
        style: 'dark',
        icon: 'label',
      }),
      new MenuIcon({
        key: 'help',
        label: 'user.help',
        order: 4,
        style: 'dark',
        icon: 'help',
        callback: () => window.open('https://help.merlinsoftware.be'),
      }),
    ];

    return new Menu({
      elements: menu.sort((a, b) => a.order - b.order),
      direction: 'vertical',
    });
  }

  public getDisconnectionMenu() {
    const menu = [
      new MenuIcon({
        key: 'logout',
        label: 'user.logout',
        order: 4,
        style: 'dark',
        icon: 'log-out',
        callback: () => this.disconnect(),
      }),
    ];

    return new Menu({
      elements: menu.sort((a, b) => a.order - b.order),
      direction: 'vertical',
    });
  }

  public navigateToEditProfile() {
    this.navigateEditEvent.emit();
  }

  private _fetch() {
    this.requestState.asked();
    this._usersService.fetchCurrentUser$().subscribe({
      complete: () => this.requestState.completed(),
      error: (error: HttpErrorResponse) => {
        this.requestState.onError(error.status, error.statusText);
      },
    });
  }
}
